// @flow
import type { Element } from 'react';

import React from 'react';
import { Card, CardBody, Col, Form, FormText, Row } from 'reactstrap';
import {
  COLLAPSE_CAPACITY_BETA,
  COLLAPSE_CAPACITY_MEDIAN,
  COLLAPSE_DEFINITION_METHOD,
  FEMA_154_SCORE,
  FEMA_154_SCORE_CALCULATION_METHOD,
  PROB_COLLAPSE_MCE,
} from '@hbrisk/sp3-risk-model-support/models/app/attributes/names/index.js';
import BackButton from '#components/pages/Models/Model/sections/support/buttons/BackButton.jsx';
import NextButton from '#components/pages/Models/Model/sections/support/buttons/NextButton.jsx';
import { Field, FormGroup } from '#components/pages/Models/Model/support/form/index.js';
import Tip from '#components/support/tooltips/Tip/index.jsx';

type Props = {|
  fieldsByName: Object,
  form: string,
  handleSubmit: Function,
  heading: string,
  onBack: Function,
  onNext: Function,
  showProbCollapseMceRO: Boolean,
  showCollapseMedianRO: Boolean,
  probCollapseMce: number,
  collapseCapacityMedian: number,
  showMessage: Boolean,
|};

const BuildingStabilitySection = (props: Props): Element<'div'> => {
  const {
    fieldsByName,
    handleSubmit,
    heading,
    form,
    onBack,
    onNext,
    showProbCollapseMceRO,
    showCollapseMedianRO,
    probCollapseMce,
    collapseCapacityMedian,
    showMessage,
  } = props;

  return (
    <div>
      <h2>{heading}</h2>
      <Form noValidate onSubmit={handleSubmit}>
        {showMessage && (
          <FormText color="muted" className="pb-2">
            *Because &ldquo;Include collapse&rdquo; is not selected in &ldquo;Analysis
            Options&rdquo;, parameters in this section will not affect your analysis results
          </FormText>
        )}
        <FormGroup tag="fieldset">
          <legend>Collapse Information</legend>
          <FormGroup form={form} fields={[COLLAPSE_DEFINITION_METHOD]}>
            <Row>
              <Col lg="6">
                <Field field={fieldsByName[COLLAPSE_DEFINITION_METHOD]} />
              </Col>
            </Row>
          </FormGroup>
        </FormGroup>
        <Card>
          <CardBody>
            <Row>
              <Col lg="9">
                <FormGroup form={form} fields={[FEMA_154_SCORE_CALCULATION_METHOD]}>
                  <Field field={fieldsByName[FEMA_154_SCORE_CALCULATION_METHOD]} />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col lg="6">
                <FormGroup form={form} fields={[FEMA_154_SCORE]}>
                  <Field field={fieldsByName[FEMA_154_SCORE]} />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col lg="6">
                <FormGroup form={form} fields={[PROB_COLLAPSE_MCE]}>
                  <Field field={fieldsByName[PROB_COLLAPSE_MCE]} />
                </FormGroup>
                {showProbCollapseMceRO && (
                  <div id="probCollapseMceRO" className="pt-2 pb-3">
                    <div>
                      Probability of Collapse at MCE Intensity <Tip tipId={PROB_COLLAPSE_MCE} />
                    </div>
                    {probCollapseMce !== null && <div>{probCollapseMce} %</div>}
                  </div>
                )}
              </Col>
            </Row>
            <Row>
              <Col lg="6">
                <FormGroup form={form} fields={[COLLAPSE_CAPACITY_MEDIAN]}>
                  <Field field={fieldsByName[COLLAPSE_CAPACITY_MEDIAN]} />
                </FormGroup>
                {showCollapseMedianRO && (
                  <div id="collapseCapacityMedianRO" className="pt-2 pb-3">
                    <div>
                      Median Collapse Capacity <Tip tipId={COLLAPSE_CAPACITY_MEDIAN} />
                    </div>
                    {collapseCapacityMedian !== null && <div>{collapseCapacityMedian} g</div>}
                  </div>
                )}
              </Col>
            </Row>
            <Row>
              <Col lg="6">
                <FormGroup form={form} fields={[COLLAPSE_CAPACITY_BETA]}>
                  <Field field={fieldsByName[COLLAPSE_CAPACITY_BETA]} />
                </FormGroup>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <BackButton onClick={onBack} />
        <NextButton onClick={onNext} />
      </Form>
    </div>
  );
};

export default BuildingStabilitySection;
